import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User } from 'lucide-react';
import api from '../lib/api';

const TEAL = '#00a99d';
const NAVY = '#1a1a2e';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm the Realty JAG assistant. How can I help you today?" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Generate or restore session ID
  useEffect(() => {
    const stored = sessionStorage.getItem('rjag_chat_session');
    if (stored) {
      setSessionId(stored);
      // Load history
      api.get(`/chatbot/history/${stored}`).then(res => {
        if (res.data.messages?.length > 0) {
          setMessages([
            { role: 'assistant', content: "Hi! I'm the Realty JAG assistant. How can I help you today?" },
            ...res.data.messages,
          ]);
        }
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const res = await api.post('/chatbot/message', {
        message: text,
        session_id: sessionId,
      });
      const newSessionId = res.data.session_id;
      if (!sessionId) {
        setSessionId(newSessionId);
        sessionStorage.setItem('rjag_chat_session', newSessionId);
      }
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I'm having trouble responding right now. Please try again in a moment."
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating bubble */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="chatbot-toggle-btn"
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
            width: 56, height: 56, borderRadius: '50%',
            background: TEAL, border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(0,169,157,0.4)',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          <MessageCircle style={{ width: 26, height: 26, color: '#fff' }} />
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div
          data-testid="chatbot-window"
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
            width: 380, maxHeight: 520, borderRadius: 16,
            background: '#fff', border: '1px solid #e5e7eb',
            boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
            animation: 'chatSlideUp 0.25s ease-out',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 18px', background: NAVY, color: '#fff', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Bot style={{ width: 18, height: 18, color: '#fff' }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Realty JAG Assistant</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>Powered by AI</div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              data-testid="chatbot-close-btn"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#94a3b8', padding: 4, borderRadius: 6,
              }}
            >
              <X style={{ width: 20, height: 20 }} />
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '16px 14px',
            display: 'flex', flexDirection: 'column', gap: 12,
            minHeight: 300,
          }}>
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  display: 'flex', gap: 8,
                  flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  background: msg.role === 'user' ? '#e5e7eb' : `${TEAL}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {msg.role === 'user'
                    ? <User style={{ width: 14, height: 14, color: '#64748b' }} />
                    : <Bot style={{ width: 14, height: 14, color: TEAL }} />
                  }
                </div>
                <div style={{
                  maxWidth: '75%', padding: '10px 14px', borderRadius: 14,
                  fontSize: 13, lineHeight: 1.55, wordBreak: 'break-word',
                  background: msg.role === 'user' ? TEAL : '#f7f8fa',
                  color: msg.role === 'user' ? '#fff' : NAVY,
                  borderBottomRightRadius: msg.role === 'user' ? 4 : 14,
                  borderBottomLeftRadius: msg.role === 'user' ? 14 : 4,
                }}>
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  background: `${TEAL}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Bot style={{ width: 14, height: 14, color: TEAL }} />
                </div>
                <div style={{
                  padding: '10px 14px', borderRadius: 14, borderBottomLeftRadius: 4,
                  background: '#f7f8fa', display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <Loader2 style={{ width: 14, height: 14, color: TEAL, animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: 13, color: '#94a3b8' }}>Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '12px 14px', borderTop: '1px solid #e5e7eb',
            display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0,
          }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything..."
              data-testid="chatbot-input"
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 10,
                border: '1px solid #e5e7eb', fontSize: 13,
                outline: 'none', background: '#f7f8fa', color: NAVY,
              }}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              data-testid="chatbot-send-btn"
              style={{
                width: 38, height: 38, borderRadius: 10,
                background: input.trim() && !loading ? TEAL : '#e5e7eb',
                border: 'none', cursor: input.trim() && !loading ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.2s',
              }}
            >
              <Send style={{ width: 16, height: 16, color: input.trim() && !loading ? '#fff' : '#94a3b8' }} />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default ChatBot;
